import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Container } from 'react-bootstrap';
import { SortableTile } from './SortableTile';
import { TileDisplay, Tile } from './TileDisplay';
import { useState } from 'react';
import { Spinner } from 'react-bootstrap';

export interface DashboardGridProps {
  tiles: Tile[];
  onTilesReorder: (tiles: Tile[]) => void;
  isLoading?: boolean;
}

/**
 * DnD-enabled grid component for displaying dashboard tiles.
 * Handles reordering tiles and renders them with the appropriate chart type.
 */
export function DashboardGrid({ tiles, onTilesReorder, isLoading = false }: DashboardGridProps) {
  const [activeTile, setActiveTile] = useState<Tile | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const tile = tiles.find((t) => t.id === (event.active.id as string));
    if (tile) setActiveTile(tile);
  };

  const handleDragOver = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = tiles.findIndex((t) => t.id === active.id);
      const newIndex = tiles.findIndex((t) => t.id === over.id);
      onTilesReorder(arrayMove(tiles, oldIndex, newIndex));
    }
  };

  const handleDragEnd = () => {
    setActiveTile(null);
  };

  const getColumnClass = (chartType: 'line' | 'bar' | 'single') => {
    return `col-${chartType === 'single' ? '6' : '12'} col-md-${chartType === 'single' ? '3' : '8'} col-lg-${chartType === 'single' ? '2' : '6'} mb-4`;
  };

  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  };

  if (tiles.length === 0) {
    return (
      <Container style={{ maxWidth: '100%' }}>
        <div className="text-center p-5 rounded my-3 mx-3">
          {isLoading ? (
            <>
              <Spinner animation="border" role="status" className="text-muted" />
              <h4 className="mt-3">Loading data&hellip;</h4>
            </>
          ) : (
            <>
              <i className="bi bi-grid-3x3-gap fs-1 text-muted"></i>
              <h4 className="mt-3">No tiles available</h4>
              <p className="text-muted">Click 'Add a new tile' to create your first dashboard item</p>
            </>
          )}
        </div>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: '100%' }}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="dashboard-grid">
          <SortableContext items={tiles.map((t) => t.id)} strategy={() => null}>
            <div className="row">
              {tiles.map((tile) => (
                <SortableTile key={tile.id} id={tile.id} className={getColumnClass(tile.chartType)}>
                  <TileDisplay tile={tile} />
                </SortableTile>
              ))}
            </div>
          </SortableContext>
        </div>

        <DragOverlay dropAnimation={dropAnimation}>
          {activeTile ? (
            <div style={{ width: '100%', height: '100%' }}>
              <SortableTile id={''}>
                <TileDisplay tile={activeTile} />
              </SortableTile>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Container>
  );
}
